import React, { useEffect, useMemo } from 'react'

// Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Typography,
  Box,
} from '@mui/material'
import { Close, Delete } from '@mui/icons-material'

// Types
import { AttachmentPreviewModalProps } from './types'

// Logic
import { useAttachmentPreviewModalLogic } from './AttachmentPreviewModal.logic'

// Styles
import styles from './AttachmentPreviewModal.module.scss'

export const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = (
  props
) => {
  const { handleRemove, isImage, isPdf } = useAttachmentPreviewModalLogic(props)
  const { attachment, open, onClose } = props

  // Create blob URL for PDF using useMemo to avoid infinite loops
  const pdfBlobUrl = useMemo(() => {
    if (!attachment) return null

    // Check if file is PDF
    const extension = attachment.filename.split('.').pop()?.toLowerCase()
    const isPdfFile =
      extension === 'pdf' || attachment.mimetype === 'application/pdf'

    if (!isPdfFile) return null

    try {
      // Convert base64 to blob
      const byteCharacters = atob(attachment.content)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })
      return URL.createObjectURL(blob)
    } catch (error) {
      console.error('Error creating PDF blob URL:', error)
      return null
    }
  }, [attachment])

  // Cleanup blob URL when component unmounts or pdfBlobUrl changes
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfBlobUrl])

  if (!attachment) return null

  const renderPreviewContent = () => {
    if (isImage(attachment.mimetype, attachment.filename)) {
      return (
        <div className={styles.imagePreviewContainer}>
          <img
            src={`data:${attachment.mimetype};base64,${attachment.content}`}
            alt={attachment.filename}
            className={styles.previewImage}
          />
        </div>
      )
    }

    if (isPdf(attachment.mimetype, attachment.filename)) {
      return (
        <div className={styles.pdfPreviewContainer}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              className={styles.pdfIframe}
              title={attachment.filename}
            />
          ) : (
            <div className={styles.loadingContainer}>
              <Typography>Loading PDF...</Typography>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className={styles.fileInfoContainer}>
        <Typography variant="h6" className={styles.fileInfoTitle}>
          File Information
        </Typography>
        <Box className={styles.fileDetails}>
          <Typography>
            <strong>Name:</strong> {attachment.filename}
          </Typography>
          <Typography>
            <strong>Type:</strong> {attachment.mimetype}
          </Typography>
          <Typography>
            <strong>Size:</strong> {(attachment.size / 1024).toFixed(2)} KB
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          className={styles.previewUnavailable}
        >
          Preview not available for this file type
        </Typography>
      </div>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className={styles.attachmentPreviewModal}
    >
      <DialogTitle className={styles.modalHeader}>
        <Typography variant="h6" component="div" className={styles.modalTitle}>
          {attachment.filename}
        </Typography>
        <Box className={styles.headerActions}>
          <IconButton
            onClick={handleRemove}
            className={`${styles.actionButton} ${styles.removeButton}`}
            title="Remove"
          >
            <Delete />
          </IconButton>
          <IconButton
            onClick={onClose}
            className={`${styles.actionButton} ${styles.closeButton}`}
            title="Close"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={styles.modalContent}>
        {renderPreviewContent()}
      </DialogContent>

      <Box className={styles.modalFooter}>
        <Button
          variant="outlined"
          color="error"
          onClick={handleRemove}
          startIcon={<Delete />}
        >
          Remove
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Dialog>
  )
}
